import React from "react";
import { Box, Typography } from "@mui/material";
import MilestoneTaskItem from "./MilestoneTaskItem";
import MilestoneTaskMenu from "./MilestoneTaskMenu";
import { useMilestoneTaskMenu } from "../hooks/useMilestoneTaskMenu";
import { MilestoneTaskListProps } from "../types";

const MilestoneTaskList: React.FC<MilestoneTaskListProps> = ({
     tasks,
     taskStates,
     userId,
     handleTaskCheckboxChange,
     updateMilestoneTasks,
     automatedTasks,
     saleDate,
     closeOfEscrowActual,
     closeOfEscrowEstimated,
     homeSiteId,
     divisionCode,
}) => {
     // Move hook call to the top to follow React hooks rules
     const {
          taskMenuAnchorEl,
          isDatePickerOpen,
          selectedDate,
          handleTaskMenuOpen,
          handleTaskMenuClose,
          handleDateChange,
          handleMenuAction,
     } = useMilestoneTaskMenu({
          userId,
          updateMilestoneTasks,
          saleDate,
          closeOfEscrowActual,
          closeOfEscrowEstimated,
          homeSiteId,
          divisionCode,
     });

     // Add validation to prevent rendering issues
     if (!tasks || !Array.isArray(tasks)) {
          return (
               <Typography variant="body2" sx={styles.noTasks}>
                    Invalid tasks data.
               </Typography>
          );
     }

     if (!automatedTasks || !Array.isArray(automatedTasks)) {
          return (
               <Typography variant="body2" sx={styles.noTasks}>
                    Invalid automated tasks data.
               </Typography>
          );
     }

     if (tasks.length === 0) {
          return (
               <Typography variant="body2" sx={styles.noTasks}>
                    No tasks available.
               </Typography>
          );
     }

     return (
          <Box>
               {tasks.map((task, idx) => {
                    if (!task || !task.divisionTemplate) {
                         return null;
                    }

                    return (
                         <React.Fragment key={`${task.divisionTemplate.taskName}-${idx}`}>
                              <MilestoneTaskItem
                                   task={task}
                                   taskState={taskStates[task.divisionTemplate.taskName]}
                                   onCheckboxChange={() => {
                                        handleTaskCheckboxChange(task);
                                   }}
                                   onMenuOpen={(event) =>
                                        !task.divisionTemplate?.automated && handleTaskMenuOpen(event, task.divisionTemplate.taskName, tasks)
                                   }
                              />
                              <MilestoneTaskMenu
                                   anchorEl={taskMenuAnchorEl[task.divisionTemplate.taskName] || null}
                                   isOpen={Boolean(taskMenuAnchorEl[task.divisionTemplate.taskName])}
                                   onClose={() => handleTaskMenuClose(task.divisionTemplate.taskName)}
                                   task={task}
                                   isDatePickerOpen={isDatePickerOpen}
                                   selectedDate={selectedDate}
                                   onDateChange={handleDateChange}
                                   onMenuAction={handleMenuAction}
                              />
                         </React.Fragment>
                    );
               })}
          </Box>
     );
};

const styles = {
     noTasks: {
          marginLeft: "16px",
          color: "#999",
     },
};

export default React.memo(MilestoneTaskList, (prevProps, nextProps) => {
     // Custom comparison function to prevent unnecessary re-renders
     return (
          prevProps.userId === nextProps.userId &&
          prevProps.saleDate === nextProps.saleDate &&
          prevProps.closeOfEscrowActual === nextProps.closeOfEscrowActual &&
          prevProps.closeOfEscrowEstimated === nextProps.closeOfEscrowEstimated &&
          prevProps.tasks === nextProps.tasks &&
          prevProps.taskStates === nextProps.taskStates &&
          prevProps.automatedTasks === nextProps.automatedTasks &&
          prevProps.handleTaskCheckboxChange === nextProps.handleTaskCheckboxChange &&
          prevProps.updateMilestoneTasks === nextProps.updateMilestoneTasks
     );
}); 